import { forwardRef, useId, type SelectHTMLAttributes } from "react";
import { ChevronDown } from "lucide-react";
import { cn } from "../../lib/cn";

export interface SelectProps extends Omit<SelectHTMLAttributes<HTMLSelectElement>, "size"> {
  label?: string;
  helperText?: string;
}

/**
 * A native <select> that matches Input's height, radius, border and focus treatment.
 *
 * Native rather than a Radix listbox on purpose: the filter toolbar's selects are simple value
 * pickers, and keeping them native preserves the platform's own keyboard and mobile behaviour
 * for free. Only the chrome is restyled — `appearance-none` plus our own chevron, since the
 * default arrow cannot be themed and was the most visible mismatch in the toolbar row.
 */
export const Select = forwardRef<HTMLSelectElement, SelectProps>(
  ({ label, helperText, id, className, children, ...rest }, ref) => {
    const generatedId = useId();
    const selectId = id ?? generatedId;

    return (
      <div className="flex min-w-0 flex-col gap-1.5">
        {label && (
          <label htmlFor={selectId} className="text-sm font-medium text-text">
            {label}
          </label>
        )}
        <div className="relative">
          <select
            ref={ref}
            id={selectId}
            className={cn(
              "h-10 w-full appearance-none rounded-control border border-border bg-surface",
              "pl-3 pr-9 text-sm text-text",
              "transition-colors duration-fast ease-standard",
              "hover:border-border focus:border-accent focus:ring-2 focus:ring-accent/20",
              "disabled:cursor-not-allowed disabled:opacity-50",
              className,
            )}
            {...rest}
          >
            {children}
          </select>
          <ChevronDown
            aria-hidden
            className="pointer-events-none absolute right-3 top-1/2 size-4 -translate-y-1/2 text-text-muted"
          />
        </div>
        {helperText && <p className="text-xs text-text-muted">{helperText}</p>}
      </div>
    );
  },
);
Select.displayName = "Select";
